import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { EnvironmentService } from '@core/config/environment.service';

export type PrimitiveParam = string | number | boolean;

/** Typed query params for GET / DELETE */
export type QueryParams = Record<string, PrimitiveParam | PrimitiveParam[] | undefined | null>;

/**
 * Thin wrapper over `HttpClient` with remmi-v16-style base URL handling,
 * without caching/inflight maps (add per-feature when requirements are clear).
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvironmentService);

  /** Resolved API root including version prefix. */
  get apiRoot(): string {
    return this.env.apiBaseUrl;
  }

  get<T>(path: string, params?: QueryParams, headers?: Record<string, string>): Observable<T> {
    return this.http.get<T>(this.resolve(path), {
      params: this.toParams(params),
      headers: this.mergeHeaders(headers),
    });
  }

  post<T>(
    path: string,
    body?: unknown,
    options?: { headers?: Record<string, string>; withCredentials?: boolean },
  ): Observable<T> {
    const { headers: extra, withCredentials } = options ?? {};
    return this.http.post<T>(this.resolve(path), body, {
      headers: this.mergeHeaders(extra),
      ...(withCredentials !== undefined ? { withCredentials } : {}),
    });
  }

  put<T>(path: string, body?: unknown, headers?: Record<string, string>): Observable<T> {
    return this.http.put<T>(this.resolve(path), body, {
      headers: this.mergeHeaders(headers),
    });
  }

  patch<T>(path: string, body?: unknown, headers?: Record<string, string>): Observable<T> {
    return this.http.patch<T>(this.resolve(path), body, {
      headers: this.mergeHeaders(headers),
    });
  }

  delete<T>(path: string, params?: QueryParams, body?: unknown): Observable<T> {
    return this.http.delete<T>(this.resolve(path), {
      params: this.toParams(params),
      body,
      headers: this.mergeHeaders(),
    });
  }

  private resolve(relativePath: string): string {
    const trimmed = relativePath.replace(/^\/+/, '');
    return `${this.apiRoot}/${trimmed}`;
  }

  private toParams(params?: QueryParams): HttpParams | undefined {
    if (!params) {
      return undefined;
    }
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) {
        continue;
      }
      if (Array.isArray(value)) {
        value.forEach((v) => {
          httpParams = httpParams.append(key, String(v));
        });
      } else {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return httpParams;
  }

  private mergeHeaders(extra?: Record<string, string>): HttpHeaders | undefined {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (!extra) {
      return headers;
    }
    for (const [k, v] of Object.entries(extra)) {
      headers = headers.set(k, v);
    }
    return headers;
  }
}
